import type { TrackPoint, VisitRecord } from './types.js'

// ── sql.js loader (shared WASM from public/) ──

type SqlJsStatic = import('sql.js').SqlJsStatic

let sqlPromise: Promise<SqlJsStatic> | null = null

export function initSql(): Promise<SqlJsStatic> {
  if (!sqlPromise) {
    sqlPromise = import('sql.js').then((mod) => mod.default({ locateFile: () => '/sql-wasm.wasm' }))
  }
  return sqlPromise
}

// ── time utilities ──

/**
 * Parses a SQLite DATETIME string. The iOS app stores times in UTC without a
 * Z suffix ("2024-05-07 09:15:13.003"), so we append Z to force UTC parsing.
 */
export function toEpoch(ts: string): number {
  return new Date(ts.replace(' ', 'T') + 'Z').getTime()
}

const SG_OFFSET_MS = 8 * 3600000

/** Formats an epoch (ms) as "YYYY-MM-DD HH:mm" in SGT. */
export function fmtSG(epoch: number): string {
  const d = new Date(epoch + SG_OFFSET_MS)
  const iso = d.toISOString()
  return iso.slice(0, 10) + ' ' + iso.slice(11, 16)
}

/** Human readable duration. */
export function fmtDuration(ms: number): string {
  const s = Math.round(ms / 1000)
  if (s < 60) return s + 's'
  if (s < 3600) return (s / 60).toFixed(1) + 'min'
  return (s / 3600).toFixed(2) + 'h'
}

// ── SQL extraction ──

export function extractTrackPoints(db: import('sql.js').Database): TrackPoint[] {
  const results = db.exec(`
    SELECT id, latitude, longitude, altitude, horizontalAccuracy, speed, course,
           timestamp, source, segmentIndex
    FROM TrackPoint ORDER BY timestamp ASC
  `)
  if (results.length === 0) return []
  const cols = results[0]?.columns ?? []
  const rows = results[0]?.values ?? []
  return rows.map((row) => {
    const obj: Record<string, unknown> = {}
    cols.forEach((c, idx) => (obj[c] = row[idx]))
    return obj as unknown as TrackPoint
  })
}

export function extractVisitRecords(db: import('sql.js').Database): VisitRecord[] {
  // Some older DBs may not have this table — guard.
  const tbl = db.exec(`SELECT name FROM sqlite_master WHERE type='table' AND name='VisitRecords'`)
  if (tbl.length === 0) return []

  const results = db.exec(`
    SELECT id, arrivalDate, departureDate, latitude, longitude, horizontalAccuracy, invalid
    FROM VisitRecords
    WHERE invalid = 0
    ORDER BY arrivalDate ASC
  `)
  if (results.length === 0) return []
  const cols = results[0]?.columns ?? []
  const rows = results[0]?.values ?? []
  return rows.map((row) => {
    const obj: Record<string, unknown> = {}
    cols.forEach((c, idx) => (obj[c] = row[idx]))
    return obj as unknown as VisitRecord
  })
}

/**
 * Deduplicate visits that repeat the same (arrival, departure, lat, lng).
 * Lat/lng are rounded to 5 decimals (~1m) so near-identical records collapse.
 */
export function dedupeVisits(visits: VisitRecord[]): VisitRecord[] {
  const seen = new Set<string>()
  const out: VisitRecord[] = []
  for (const v of visits) {
    const key = [v.arrivalDate, v.departureDate ?? 'null', v.latitude.toFixed(5), v.longitude.toFixed(5)].join('|')
    if (seen.has(key)) continue
    seen.add(key)
    out.push(v)
  }
  return out
}

// ── filtering ──

export function filterTrackPointsByWindow(points: TrackPoint[], windowStart: number, windowEnd: number): TrackPoint[] {
  return points.filter((p) => {
    const t = toEpoch(p.timestamp)
    return t >= windowStart && t <= windowEnd
  })
}

/**
 * A visit is in the window if either its arrival or departure overlaps it.
 *
 * IMPORTANT: visits with `departureDate = null` are stale records (the
 * departure event was never received). We only include such visits if their
 * arrival is itself inside the window — otherwise we'd drag in years of
 * cross-city history every time the user opens the page.
 */
export function filterVisitsByWindow(visits: VisitRecord[], windowStart: number, windowEnd: number): VisitRecord[] {
  return visits.filter((v) => {
    const arr = toEpoch(v.arrivalDate)
    if (v.departureDate) {
      const dep = toEpoch(v.departureDate)
      return dep >= windowStart && arr <= windowEnd
    }
    return arr >= windowStart && arr <= windowEnd
  })
}

/** Combined [min, max] epoch across trackpoints and visits. Returns [0, 0] if both empty. */
export function computeDataTimeRange(points: TrackPoint[], visits: VisitRecord[]): [number, number] {
  let min = Infinity
  let max = -Infinity
  for (const p of points) {
    const t = toEpoch(p.timestamp)
    if (t < min) min = t
    if (t > max) max = t
  }
  for (const v of visits) {
    const a = toEpoch(v.arrivalDate)
    if (a < min) min = a
    if (a > max) max = a
    if (v.departureDate) {
      const d = toEpoch(v.departureDate)
      if (d > max) max = d
    }
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [0, 0]
  return [min, max]
}

// ── centroid (for map initial view) ──

export function computeCentroid(points: TrackPoint[], visits: VisitRecord[]): { lat: number; lng: number } | null {
  let sumLat = 0
  let sumLng = 0
  let n = 0
  for (const p of points) {
    sumLat += p.latitude
    sumLng += p.longitude
    n++
  }
  for (const v of visits) {
    sumLat += v.latitude
    sumLng += v.longitude
    n++
  }
  if (n === 0) return null
  return { lat: sumLat / n, lng: sumLng / n }
}
